import { faExchangeAlt, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientRateAssignments } from '../../hooks/useClientRateAssignments';
import { ChangeRateModal } from './ChangeRateModal';

interface RateAssignmentsTabProps {
  teamId: number;
}

export const RateAssignmentsTab = ({ teamId }: RateAssignmentsTabProps) => {
  const { t, i18n } = useTranslation('viewClientModule');
  const { rateAssignments, isLoading, error } = useClientRateAssignments(teamId);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'es' ? es : undefined;
      return format(date, 'dd/MM/yyyy HH:mm', { locale });
    } catch {
      return '-';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'scheduled':
        return 'secondary';
      case 'inactive':
        return 'outline';
      case 'ended':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getChangeTypeVariant = (changeType: string) => {
    switch (changeType) {
      case 'upgrade':
        return 'default';
      case 'downgrade':
        return 'secondary';
      case 'initial':
        return 'outline';
      case 'renewal':
        return 'default';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <p>{t('error.generic')}</p>
      </div>
    );
  }

  const ChangeRateButton = (
    <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)}>
      <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
      {t('changeRate.button')}
    </Button>
  );

  if (!rateAssignments || rateAssignments.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">{ChangeRateButton}</div>
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground border border-dashed border-card-border rounded-lg">
          <FontAwesomeIcon icon={faExchangeAlt} className="w-10 h-10 mb-3 opacity-50" />
          <p className="mb-4">{t('rateAssignments.empty')}</p>
          <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
            {t('changeRate.buttonFirst')}
          </Button>
        </div>
        <ChangeRateModal open={isModalOpen} onOpenChange={setIsModalOpen} teamId={teamId} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">{ChangeRateButton}</div>
      {rateAssignments.map((assignment) => (
        <div
          key={assignment.id}
          className={`p-4 rounded-lg border ${
            assignment.status === 'active' ? 'border-primary bg-primary/5' : 'border-card-border bg-card'
          }`}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-medium text-foreground">{assignment.rate.name}</h4>
              {assignment.reason && <p className="text-sm text-muted-foreground mt-1">{assignment.reason}</p>}
            </div>
            <div className="flex gap-2">
              <Badge variant={getStatusVariant(assignment.status)}>
                {t(`rateAssignments.status.${assignment.status}`)}
              </Badge>
              <Badge variant={getChangeTypeVariant(assignment.change_type)}>
                {t(`rateAssignments.changeType.${assignment.change_type}`)}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">{t('rateAssignments.effectiveFrom')}</p>
              <p className="font-medium">{formatDate(assignment.effective_from)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('rateAssignments.effectiveTo')}</p>
              <p className="font-medium">
                {assignment.effective_to ? formatDate(assignment.effective_to) : t('rateAssignments.indefinite')}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('rateAssignments.appliedAt')}</p>
              <p className="font-medium">{assignment.applied_at ? formatDate(assignment.applied_at) : '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('rateAssignments.appliedBy')}</p>
              <p className="font-medium">{assignment.user?.name || '-'}</p>
            </div>
          </div>
        </div>
      ))}
      <ChangeRateModal open={isModalOpen} onOpenChange={setIsModalOpen} teamId={teamId} />
    </div>
  );
};
